import { buildMessagesUrl } from './buildMessagesUrl';
import type {
  PublishStreamingMessageParams,
  SendMessageRequest,
  SendMessageResponse,
} from './sendMessage.types';
import { parseSendMessageResponse } from './sendMessageResponse';
import {
  fetchWithSidekickAuth,
  SidekickHttpError,
  withSidekickTransportTimeout,
} from './sidekickHttp';

export type {
  PublishStreamingMessageParams,
  SendMessageRequest,
  SendMessageResponse,
} from './sendMessage.types';

/**
 * Publishes a user turn on the open SSE channel:
 * `POST {apiBaseUrl}/conversations/{id}/messages` with
 * `Accept: text/event-stream` and a {@link SendMessageRequest} body.
 * Uses the same JWT Bearer auth + 401/409 retry policy as
 * {@link subscribe} (see `publishStreamingMessage.ts` colocated with
 * `openSseConnection.ts` subscribe — kept as a separate module so
 * subscribe stays GET-only and publish stays POST-only).
 */
export const publishStreamingMessage = async (
  params: PublishStreamingMessageParams
): Promise<SendMessageResponse> => {
  const {
    apiBaseUrl,
    conversationId,
    signal,
    content,
    inputType,
    modelHint,
    promptAlias,
    interface: interfaceName,
    fleetId,
  } = params;

  const url = buildMessagesUrl(apiBaseUrl, conversationId);
  const body: SendMessageRequest = {
    content,
    ...(inputType !== undefined ? { inputType } : {}),
    ...(modelHint !== undefined ? { modelHint } : {}),
    ...(promptAlias !== undefined ? { promptAlias } : {}),
    ...(interfaceName !== undefined ? { interface: interfaceName } : {}),
    ...(fleetId !== undefined ? { fleetId } : {}),
  };

  const response = await fetchWithSidekickAuth({
    url,
    signal,
    init: {
      method: 'POST',
      headers: {
        Accept: 'text/event-stream',
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(body),
    },
  });

  let parsed: unknown;
  try {
    parsed = await withSidekickTransportTimeout({
      operation: response.json(),
      message: 'Sidekick SSE: SendMessageResponse JSON timed out',
    });
  } catch (parseErr) {
    if (parseErr instanceof SidekickHttpError) {
      throw parseErr;
    }
    throw new Error(
      parseErr instanceof Error
        ? parseErr.message
        : 'Sidekick SSE: invalid SendMessageResponse JSON'
    );
  }
  return parseSendMessageResponse(parsed);
};
